<template>
  <div class="document-video-document">
    <!-- Video Dosyası Yükleme Alanı -->
    <div class="uploadOuter">
      <label class="btn btn-info" for="uploadVideoFile" @click="onUploadClick">
        <i class="uil-upload"></i> {{ $t("general.upload") }}
      </label>
      <strong>{{ $t("general.or") }}</strong>
      <span class="dragBox">
        <i :class="icon"></i>
        {{ $t("documents.video_document_drag_drop") }}
        <input
          id="uploadVideoFile"
          :accept="acceptableFileType"
          draggable="true"
          type="file"
          @click="onUploadClick"
          @change="handleFileUpload"
          @dragenter="drag"
          @dragleave="drop"
          @dragover.prevent
        />
      </span>
    </div>

    <!-- Dosya oynatilabilir mi diye kontrol edilirken: buyuk dosyada metadata
         okumak bir kac saniye surebiliyor, o arada ekran sessiz kalmasin -->
    <div v-if="isInspecting" class="alert alert-info document-video-document__state">
      <i class="uil-spinner-alt mr-2"></i>
      {{ $t("general.video_being_checked") }}
    </div>

    <!-- Hata, sayfanin tepesindeki banner yerine yukleme alaninin yaninda:
         form uzun, tepeye kaydirmak kullaniciyi yukleme kutusundan
         uzaklastiriyordu. Bir sonraki dosya secimine kadar ekranda kalir. -->
    <div v-else-if="uploadErrorKey" class="alert alert-danger document-video-document__state">
      <i class="uil-exclamation-triangle mr-2"></i>
      <span>
        {{ uploadError }}
        <small v-if="checkDetail" class="document-video-document__detail">({{ checkDetail }})</small>
      </span>
    </div>

    <!-- Kontrol gectiginde de gorunur sonuc: hem kullanici videonun
         dogrulandigini bilsin, hem de kontrolun calistigi belli olsun. -->
    <div v-else-if="checkSummary" class="alert alert-success document-video-document__state">
      <i class="uil-check-circle mr-2"></i>
      <span>{{ $t("general.video_check_passed") }} <small class="document-video-document__detail">({{ checkSummary }})</small></span>
    </div>

    <!-- Video önizleme ve ayarları (dosya yüklendikten sonra gösterilir) -->
    <div v-if="videoUrl && !isInspecting">
      <video-type-selector 
        :value="isInteractiveVideoActive"
        @input="$emit('update:isInteractiveVideoActive', $event)"
        :first_text="$t('general.standard_video')"
        :second_text="$t('general.interactive_video')" 
      />

      <div v-show="isInteractiveVideoActive" class="interactive-video-area">
        <interactive-video-player-wrapper 
          :key="videoUrl" 
          :video-url="videoUrl" 
        />
      </div>

      <div v-show="!isInteractiveVideoActive" class="standart-video-area">
        <div class="video-preview">
          <div id="preview" v-html="videoDocInfo"></div>
          <video-player :key="videoUrl" :video-url="videoUrl" />
        </div>
        <!-- Inline style'lar siniflara tasindi: checkbox `width: 2%` idi,
             375px ekranda ~7px'e dusup kullanilamaz hale geliyordu.
             <label> sarmalayici sayesinde metne tiklamak da calisiyor. -->
        <div class="video-options">
          <label class="video-option">
            <span>{{ $t('general.fast_forward_video') }}</span>
            <input
              :checked="canBeFastForwarded"
              type="checkbox"
              @change="handleFastForwardChange('start', $event.target.checked)"
            >
          </label>
          <label class="video-option">
            <span>{{ $t('general.fast_forward_when_video_completed') }}</span>
            <input
              :checked="canBeFastForwardedWhenCompleted"
              type="checkbox"
              @change="handleFastForwardChange('completed', $event.target.checked)"
            >
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VideoTypeSelector from "@/view/components/video/VideoTypeSelector.vue";
import InteractiveVideoPlayerWrapper from "@/view/components/video/interactive-video/InteractiveVideoPlayerWrapper.vue";
import VideoPlayer from "@/view/components/video/VideoPlayer.vue";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";
import { inspectVideoFile, videoFileErrorTranslationKey } from "@/utils/videoFileValidator";

export default {
  name: "DocumentVideoDocument",
  components: {
    VideoTypeSelector,
    InteractiveVideoPlayerWrapper,
    VideoPlayer
  },
  props: {
    isInteractiveVideoActive: {
      type: Boolean,
      default: false
    },
    videoUrl: {
      type: String,
      default: ""
    },
    videoDocInfo: {
      type: String,
      default: ""
    },
    canBeFastForwarded: {
      type: Boolean,
      default: false
    },
    canBeFastForwardedWhenCompleted: {
      type: Boolean,
      default: false
    },
    acceptableFileType: {
      type: String,
      default: "video/*"
    },
    icon: {
      type: String,
      default: "uil-video"
    },
    existingFile: {
      type: String,
      default: null
    },
    // 0 = sinir yok. Oynatilabilirlik kontrolunu bosa calistirmamak icin
    // kullaniliyor; boyut hatasinin mesajini parent gosteriyor.
    maxFileSize: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      _waitingForFile: false,
      isInspecting: false,
      uploadErrorKey: "",
      // Kontrol sonucu: basarili dosyada ozet, hatali dosyada teknik sebep.
      checkSummary: "",
      checkDetail: "",
    };
  },
  computed: {
    // Anahtar tutuluyor, metin degil: dil degisince mesaj da degisiyor.
    uploadError() {
      return this.uploadErrorKey ? this.$t(this.uploadErrorKey) : "";
    }
  },
  mounted() {
    if (!bridge.isNative) {
      return;
    }
    this._unsubscribePickFile = bridge.on("PICK_FILE_RESULT", (payload) => {
      if (!this._waitingForFile) return;
      this._waitingForFile = false;
      if (!payload.success || !payload.file) return;
      const { name, type, base64 } = payload.file;
      const byteString = atob(base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      this.emitUploadedFile(new File([ab], name, { type }));
    });
  },
  beforeDestroy() {
    if (this._unsubscribePickFile) {
      this._unsubscribePickFile();
    }
  },
  methods: {
    onUploadClick(event) {
      if (!bridge.isNative) {
        return;
      }
      event.preventDefault();
      this._waitingForFile = true;
      native.pickFile(this.acceptableFileType);
    },
    handleFastForwardChange(type, checked) {
      if (type === 'start' && checked) {
        this.$emit('update:canBeFastForwardedWhenCompleted', false);
      } else if (type === 'completed' && checked) {
        this.$emit('update:canBeFastForwarded', false);
      }
      
      if (type === 'start') {
        this.$emit('update:canBeFastForwarded', checked);
      } else {
        this.$emit('update:canBeFastForwardedWhenCompleted', checked);
      }
    },
    handleFileUpload(event) {
      const input = event.target;
      this.emitUploadedFile(input.files[0]).then(() => {
        // Input bosaltilmazsa ayni dosya ikinci kez secilemiyor: hatali
        // dosyayi duzeltip tekrar secen kullanicida change event'i hic
        // tetiklenmiyordu.
        input.value = "";
      });
    },
    async emitUploadedFile(file) {
      // Kontrol bitmeden yeni dosya secilebiliyor; yalnizca en son secimin
      // sonucu uygulanacak.
      const requestId = (this._inspectRequestId = (this._inspectRequestId || 0) + 1);
      this.uploadErrorKey = "";
      this.checkSummary = "";
      this.checkDetail = "";

      if (!file) {
        this.isInspecting = false;
        return false;
      }

      // Video dosyası mı kontrol et
      if (!this.compareTypeCompatibality(file, this.acceptableFileType)) {
        this.isInspecting = false;
        this.uploadErrorKey = 'general.invalid_file_type';
        return false;
      }

      // Boyut siniri asilmis dosyayi taramiyoruz: parent zaten reddedecek,
      // 300 MB'lik bir dosyanin kontrolu ~2 saniye suruyor.
      if (this.maxFileSize && file.size > this.maxFileSize) {
        this.isInspecting = false;
        this.$emit('file-uploaded', file);
        return false;
      }

      // Tip kontrolu dosyanin icerigi hakkinda bir sey soylemiyor: bozuk ya
      // da tarayicinin cozemedigi bir dosya da oradan geciyor ve sorun ancak
      // oynaticida (ya da kullanicinin karsisinda) ortaya cikiyordu.
      this.isInspecting = true;
      let result;
      try {
        result = await inspectVideoFile(file);
      } catch (error) {
        // Kontrolun kendisi patlarsa dosyayi gecirmiyoruz: oynatici da ayni
        // decoder'i kullanacagi icin orada da patlamasi beklenir.
        result = { valid: false };
      }

      // Arada baska bir dosya secildiyse bu sonuc artik gecersiz; durumu da
      // sonrasinda baslayan kontrol yonetiyor.
      if (requestId !== this._inspectRequestId) return false;
      this.isInspecting = false;

      // Tek satir log: hangi asamanin ne dedigi tarayici console'undan
      // dogrulanabilsin.
      console.info("[video-check]", file.name, result);

      if (!result.valid) {
        this.checkDetail = result.detail || "";
        this.uploadErrorKey = videoFileErrorTranslationKey(result.reason);
        return false;
      }

      this.checkSummary = this.buildCheckSummary(result);

      // Parent component'e dosyayı gönder
      this.$emit('file-uploaded', file);
      return true;
    },
    buildCheckSummary(result) {
      const parts = [];
      if (result.duration) {
        const total = Math.round(result.duration);
        const minutes = Math.floor(total / 60);
        const seconds = String(total % 60).padStart(2, "0");
        parts.push(`${minutes}:${seconds}`);
      }
      if (result.width && result.height) parts.push(`${result.width}x${result.height}`);
      return parts.join(" · ");
    },
    compareTypeCompatibality(file, acceptableType) {
      return (
        acceptableType.includes(file.type) ||
        acceptableType.includes(file.name.split(".").pop()) ||
        (acceptableType.split("/")[0] == file.type.split("/")[0] &&
          file.type.split("/")[0].toLowerCase() != "application")
      );
    },
    drag(event) {
      event.target.parentNode.className = "draging dragBox";
    },
    drop(event) {
      event.target.parentNode.className = "dragBox";
    }
  }
};
</script>

<style scoped>
.standart-video-area .artplayer-app {
  max-height: 500px;
}

.dragBox input[type="file"] {
  position: absolute;
  height: 100%;
  width: 100%;
  opacity: 0;
  top: 0;
  left: 0;
}

/* line-height ile dikey ortalama yerine flex: metin sarinca da ortali kalir */
.dragBox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: auto;
  max-width: 100%;
  min-height: 100px;
  padding: 8px 16px;
  margin: 0 auto;
  position: relative;
  text-align: center;
  font-weight: bold;
  line-height: 1.4;
  color: #999;
  border: 2px dashed #ccc;
  transition: transform 0.3s;
}

/* Kontrol durumu / hata mesaji: yukleme alaniyla ayni genislikte */
.document-video-document__state {
  display: flex;
  align-items: center;
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.4;
}

.document-video-document__detail {
  opacity: 0.75;
  font-size: 12px;
  word-break: break-word;
}

.document-video-document__state .uil-spinner-alt {
  animation: document-video-document-spin 1s linear infinite;
}

@keyframes document-video-document-spin {
  to {
    transform: rotate(360deg);
  }
}

.video-options {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 32px;
  padding: 16px 0 0;
}

.video-option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  min-width: 0;
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
}

.video-option input[type="checkbox"] {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin: 0;
}

.dragBox.draging {
  transform: scale(1.05);
  border-color: #3e416d;
}

.uploadOuter strong {
  padding: 0 10px;
}

.uploadOuter {
  text-align: center;
  padding: 20px;
}

/* Interactive video yerlesimi
   Web  : degismiyor — video solda, soru paneli sagda (paneli konumlandiran
          `.questions-list { position: absolute; top:0; right:10px }` kurali
          `.video-responsive`e tutunuyor).
   Mobil: panel akisa alinip videonun ALTINA iniyor; 320px'lik overlay dar
          ekranda videoyu tamamen kapatiyordu. */
@media (max-width: 959px) {
  /* ---- Yukleme alani ---- */
  .uploadOuter {
    flex-direction: column;
    padding: 16px;
  }

  .uploadOuter .btn,
  .dragBox {
    width: 100%;
  }

  .uploadOuter .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
  }

  /* ---- Standart / Etkilesimli video butonlari: yan yana kalsin ----
     Her label min-width: 100px + genis padding tasiyordu, dar ekranda
     sigmayip alt alta duselerdi. Esit paylasip icerde sariyorlar. */
  .document-video-document ::v-deep .video-type-choice-group {
    flex-wrap: nowrap;
    gap: 8px;
  }

  .document-video-document ::v-deep .video-type-choice-group label {
    flex: 1 1 0;
    justify-content: center;
    min-width: 0;
    min-height: 48px;
    padding: 8px 10px;
    text-align: center;
  }

  .document-video-document ::v-deep .video-type-choice-group .custom-radio {
    flex-shrink: 0;
    margin-right: 8px;
  }

  .document-video-document ::v-deep .video-type-choice-group .choice-label {
    font-size: 14px;
    line-height: 1.3;
  }

  /* ---- Video yuksekligi ----
     min-height: 600px telefonda ekranin tamamini kapliyor ve videonun
     ustunde/altinda bos letterbox birakiyordu. 16:9 oranina oturtuluyor. */
  .document-video-document ::v-deep .video-js {
    height: 56.25vw;
    min-height: 0;
    max-height: 50vh;
  }

  /* Interactive ile ayni olcu. min-height: 0 SART: CSS'te min-height
     max-height'i ezer, yani tek basina max-height yazmak global
     `min-height: 600px`i kirmiyordu. */
  .standart-video-area ::v-deep .artplayer-app {
    height: 56.25vw;
    min-height: 0;
    max-height: 50vh;
  }

  /* Preview alani: global `.video-responsive { text-align: center }`
     (VideoPlayer.vue'da, scoped DEGIL) icerigi ortaliyordu. */
  .standart-video-area ::v-deep .video-responsive,
  .standart-video-area #preview {
    text-align: left;
  }

  /* ---- Interactive: video ustte, sorular altta ----
     Kap flex-column; siralama kaynak sirasina birakilmiyor. Absolute
     kardesler (.video-time-display, .marker-dropdown-container) akis
     disinda kaldigi icin etkilenmez. */
  .interactive-video-area ::v-deep .video-responsive {
    display: flex;
    flex-direction: column;
    height: auto;
    margin-bottom: 8px;
  }

  .interactive-video-area ::v-deep .video-responsive > .video-js {
    order: 1;
  }

  .interactive-video-area ::v-deep .questions-side {
    order: 2;
    /* flex: 1 1 320px idi. Kolon yonunde basis YUKSEKLIGE donusuyor,
       grow da kalan bosluğu emiyor; panel icerik 100px olsa bile ~320px
       kaliyordu. Icerige gore boyutlansin. */
    flex: 0 0 auto;
    width: 100%;
    min-width: 0;
    max-width: none;
  }

  /* position sabit kalirsa order/flex hicbir sey yapmaz */
  .interactive-video-area ::v-deep .questions-side .questions-list {
    position: static;
    width: 100%;
    min-width: 0;
    max-width: none;
    max-height: none;
    margin-top: 12px;
    padding: 12px;
  }

  /* Panelin altindaki bosluk: son soru kartinin margin'i + liste padding'i */
  .interactive-video-area ::v-deep .questions-side .question-item:last-child {
    margin-bottom: 0;
  }

  /* ---- Hizli ileri secenekleri ---- */
  .video-options {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }

  .video-option {
    justify-content: space-between;
    min-height: 48px;
    font-size: 16px;
  }

  .video-option input[type="checkbox"] {
    width: 22px;
    height: 22px;
  }
}
</style>

